
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Catrinienquery.css';

// Helper function to format date from DD-MM-YYYY to a more readable format
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [day, month, year] = parts;
  return new Date(`${year}-${month}-${day}`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Helper to format time (HH:MM)
const formatTime = (timeStr) => {
  if (!timeStr) return 'N/A';
  return timeStr;
};

// Status badge component with appropriate colors
const StatusBadge = ({ status }) => {
  const getStatusClass = (status) => {
    switch(status) {
      case 'APPROVED':
        return 'ceq-status-approved';
      case 'ASSIGNED':
        return 'ceq-status-assigned';
      case 'PENDING':
        return 'ceq-status-pending';
      case 'REJECTED':
        return 'ceq-status-rejected';
      case 'CANCELLED':
        return 'ceq-status-cancelled';
      default:
        return 'ceq-status-default';
    }
  };

  return (
    <span className={`ceq-status-badge ${getStatusClass(status)}`}>
      {status}
    </span>
  );
};

// Quotation Status Badge
const QuotationStatusBadge = ({ status }) => {
  const getStatusClass = (status) => {
    switch(status?.toUpperCase()) {
      case 'SELECTED':
        return 'ceq-quote-selected';
      case 'PENDING':
        return 'ceq-quote-pending';
      case 'REJECTED':
        return 'ceq-quote-rejected';
      case 'APPROVED':
        return 'ceq-quote-approved';
      case 'PAYMENT_COMPLETED':
        return 'ceq-quote-payment-completed';
      default:
        return 'ceq-quote-default';
    }
  };

  return (
    <span className={`ceq-quote-status-badge ${getStatusClass(status)}`}>
      {status === 'PAYMENT_COMPLETED' ? 'PAYMENT COMPLETED' : (status || 'PENDING')}
    </span>
  );
};

// Helper to get all selected dishes from items object
const getAllSelectedDishes = (items) => {
  if (!items || typeof items !== 'object') return [];
  const dishes = [];
  Object.keys(items).forEach(category => {
    if (Array.isArray(items[category])) {
      dishes.push(...items[category]);
    }
  });
  return [...new Set(dishes)];
};

// Component to display selected dishes in a clean format
const SelectedDishesSection = ({ items }) => {
  if (!items || Object.keys(items).length === 0) {
    return <div className="ceq-table-empty">No dishes selected</div>;
  }

  return (
    <div className="ceq-dishes-table">
      {Object.keys(items).map((category) => {
        const dishes = items[category];
        if (!dishes || dishes.length === 0) return null;
        return (
          <div key={category} className="ceq-dish-row">
            <div className="ceq-dish-category-cell">{category}</div>
            <div className="ceq-dish-items-cell">
              {dishes.map((dish, idx) => (
                <span key={`${category}-${dish}-${idx}`} className="ceq-dish-tag">
                  {dish}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Invoice Modal Component
const InvoiceModal = ({ order, onClose }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return 'N/A';
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handlePrint = () => {
    setTimeout(() => {
      const printContents = document.getElementById('invoice-print-area').innerHTML;
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }, 300);
  };

  return (
    <div className="ceq-modal-overlay" onClick={onClose}>
      <div className="ceq-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ceq-modal-header">
          <h3>🧾 Invoice</h3>
          <button className="ceq-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="ceq-modal-body">
          <div id="invoice-print-area" className="ceq-invoice-body">
            <div className="ceq-invoice-logo">
             <h2>{order?.companyName || "Maamaas Catering"}</h2>
              <p>Your Trusted Catering Partner</p>
            </div>
            
            <div className="ceq-invoice-info">
              <div><strong>Invoice #:</strong> {order.orderId}</div>
              <div><strong>Date:</strong> {formatDate(order.orderDateTime)}</div>
              <div><strong>Order Status:</strong> {order.orderStatus}</div>
              <div><strong>Payment Status:</strong> {order.paymentStatus}</div>
            </div>

            <div className="ceq-invoice-section">
              <h4>Vendor Details</h4>
              <p><strong>Name:</strong> {order.vendorRegisteredName}</p>
              <p><strong>Address:</strong> {order.vendorFullAddress}</p>
              <p><strong>GST IN:</strong> {order.vendorGstIn || 'N/A'}</p>
            </div>

            <div className="ceq-invoice-section">
              <h4>Customer Details</h4>
              <p><strong>Name:</strong> {order.deliveryUserName || 'Customer'}</p>
              <p><strong>Mobile:</strong> {order.mobileNo || 'N/A'}</p>
              <p><strong>Delivery Address:</strong> {order.deliveryAddress}</p>
            </div>

            <div className="ceq-invoice-section">
              <h4>Order Items</h4>
              <table className="ceq-invoice-table">
                <thead>
                  <tr><th>Item</th><th>Quantity</th><th>Price</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {order.orderItems?.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.packageName}</td>
                      <td>x{item.quantity}</td>
                      <td>{formatCurrency(item.packagePrice)}</td>
                      <td>{formatCurrency(item.packagePrice * item.quantity)}</td>
                    </tr>
                  ))}
                  {order.addOns?.map((addon, idx) => (
                    <tr key={`addon-${idx}`}>
                      <td>{addon.addOnType?.replace(/_/g, ' ')} (Add-on)</td>
                      <td>x{addon.quantity}</td>
                      <td>{formatCurrency(addon.price)}</td>
                      <td>{formatCurrency(addon.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ceq-invoice-section">
              <h4>Payment Breakdown</h4>
              <div className="ceq-invoice-breakdown">
                <div><span>Subtotal:</span><span>{formatCurrency(order.subtotal)}</span></div>
                <div><span>SGST:</span><span>{formatCurrency(order.sgst)}</span></div>
                <div><span>CGST:</span><span>{formatCurrency(order.cgst)}</span></div>
                <div><span>Delivery Fee:</span><span>{formatCurrency(order.deliveryFee)}</span></div>
                <div><span>Platform Fee:</span><span>{formatCurrency(order.platformFeeAmount)}</span></div>
                <div className="ceq-invoice-total"><span>Grand Total:</span><span>{formatCurrency(order.total)}</span></div>
                <div><span>Amount Paid:</span><span>{formatCurrency(order.amountPaid)}</span></div>
                {order.amountRemaining > 0 && (
                  <div className="ceq-invoice-remaining"><span>Remaining:</span><span>{formatCurrency(order.amountRemaining)}</span></div>
                )}
              </div>
            </div>

            {order.transactionId && (
              <div className="ceq-invoice-section">
                <h4>Transaction Details</h4>
                <p><strong>Transaction ID:</strong> {order.transactionId}</p>
                {order.razorpayPaymentId && <p><strong>Razorpay Payment ID:</strong> {order.razorpayPaymentId}</p>}
                <p><strong>Payment Method:</strong> {order.paymentMethod || 'Online Payment'}</p>
              </div>
            )}

            <div className="ceq-invoice-footer">
              <p>Thank you for choosing Maamaas Catering!</p>
              <p>For any queries, contact: support@maamaas.com</p>
            </div>
          </div>
        </div>

        <div className="ceq-modal-footer">
          <button className="ceq-invoice-print" onClick={handlePrint}>🖨️ Print Invoice</button>
          <button className="ceq-invoice-download" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Payment Component - Inline (No Popup)
const InlinePayment = ({ quotation, onPaymentSuccess, userId, authToken, onCancel }) => {
  const [paymentType, setPaymentType] = useState('PARTIAL_PAYMENT');
  const [paymentMethod, setPaymentMethod] = useState('Online_Payment');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  const paymentTypes = [
    { value: 'PARTIAL_PAYMENT', label: 'Advance Payment', description: `Pay ${quotation?.partialAmount?.toFixed(2) || '0'} (10% advance)` },
    { value: 'FINAL_PAYMENT', label: 'Full Payment', description: 'Pay the entire amount' }
  ];

  const paymentMethods = [
    { value: 'Online_Payment', label: 'Card / Net Banking' },
    { value: 'UPI', label: 'UPI' },
    { value: 'COD', label: 'Cash on Delivery' }
  ];

  const getAmountToPay = () => {
    if (paymentType === 'FINAL_PAYMENT') return quotation?.grandTotal || 0;
    if (paymentType === 'PARTIAL_PAYMENT') return quotation?.partialAmount || 0;
    return 0;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const createOrder = async (amount, paymentTypeParam) => {
    const token = authToken;

    const url = `https://backend.maamaas.com/catering/api/user/quotation/payment?quotationId=${quotation.quotationId}&leadId=${quotation.leadId}&userId=${userId}&amount=${amount}&paymentType=${paymentTypeParam}&paymentMethod=Online_Payment&paymentStatus=INITIATED`;

    console.log("🔥 URL:", url);

    try {
      const response = await axios.post(
        url,
        {
          amount,
          paymentType: paymentTypeParam,
          paymentMethod: "Online_Payment",
          paymentStatus: "INITIATED"
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      return response.data;

    } catch (err) {
  console.error("❌ Create Order Error:", err.response?.data);

  alert(err.response?.data?.message || "❌ Failed to create order");

  throw err;
}
  };

  const handleRazorpayPayment = async (orderData, amount) => {
    console.log("OrderData for Razorpay:", orderData);

    const options = {
      key: "rzp_live_RU5whSMu9rPV7s",
      amount: Math.round(amount * 100),
      currency: "INR",
      name: "Maamaas Catering",
      description: `Payment for Order #${orderData?.orderId}`,
      ...(orderData?.razorpayOrderId && {
        order_id: orderData.razorpayOrderId
      }),
      handler: async (response) => {
        setProcessing(true);

        try {
          const token = authToken;

          const verifyResponse = await axios.post(
            `https://backend.maamaas.com/catering/api/user/quotation/payment/verify`,
            {
              razorpayOrderId: response.razorpay_order_id || null,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              quotationId: quotation.quotationId,
              leadId: quotation.leadId,
              userId: userId
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          );

          console.log("✅ Verify Response:", verifyResponse.data);

          if (verifyResponse.data) {
            setPaymentSuccess(true);
            setOrderResult(verifyResponse.data);
            onPaymentSuccess(verifyResponse.data, quotation);
          }

        } catch (err) {
  console.error("❌ Verification error:", err.response?.data);

  alert(err.response?.data?.message || "❌ Payment verification failed");

  setError("Payment verification failed");
} finally {
          setProcessing(false);
        }
      },
      prefill: {
        name: quotation.vendorName,
        email: "customer@maamaas.com",
        contact: "9999999999"
      },
      theme: {
        color: "#6366f1"
      },
      modal: {
        ondismiss: () => setProcessing(false)
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const handlePayment = async () => {
    setProcessing(true);
    setError(null);

    try {
      const remainingAmount = Number(quotation?.remainingAmount || 0);

      console.log("Remaining:", remainingAmount);

      if (remainingAmount <= 0) {
  alert("✅ Already Fully Paid");
  return;
}

      const paymentTypeToSend = "FINAL_PAYMENT";
      const amount = Math.round(remainingAmount * 100) / 100;

      console.log("🔥 Sending FINAL_PAYMENT:", amount);

      const orderData = await createOrder(amount, paymentTypeToSend);
      console.log("Order Data:", orderData);
      await handleRazorpayPayment(orderData, amount);

    }  catch (err) {
  console.error("❌ Payment Error:", err.response?.data);

  alert(err.response?.data?.message || "❌ Payment failed");

  setError(err.response?.data?.message || "Payment failed");
} finally {
      setProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="ceq-payment-success-inline">
        <div className="ceq-success-icon">✓</div>
        <h4>Payment Successful!</h4>
        <p>Order ID: {orderResult?.orderId || 'N/A'}</p>
        <button className="ceq-close-payment-btn" onClick={onCancel}>Close</button>
      </div>
    );
  }

  return (
    <div className="ceq-inline-payment">
      <div className="ceq-inline-payment-header">
        <h4>💳 Make Payment</h4>
        <button className="ceq-close-payment" onClick={onCancel}>×</button>
      </div>
      
      <div className="ceq-inline-payment-body">
        <div className="ceq-payment-summary-inline">
          <div className="ceq-summary-row">
            <span>Vendor:</span>
            <strong>{quotation?.vendorName}</strong>
          </div>
          <div className="ceq-summary-row">
            <span>Total Amount:</span>
            <strong>₹{quotation?.grandTotal?.toFixed(2)}</strong>
          </div>
          <div className="ceq-summary-row">
            <span>Amount Paid:</span>
            <strong>₹{(quotation?.grandTotal - quotation?.remainingAmount).toFixed(2)}</strong>
          </div>
          <div className="ceq-summary-row">
            <span>Remaining Amount:</span>
            <strong>₹{quotation?.remainingAmount?.toFixed(2)}</strong>
          </div>
        </div>

        <div className="ceq-payment-type-group">
          <label className="ceq-label">Payment Type</label>
          <div className="ceq-payment-types-inline">
            {paymentTypes.map((type) => (
              <button
                key={type.value}
                className={`ceq-payment-type-btn-inline ${paymentType === type.value ? 'active' : ''}`}
                onClick={() => {
                  setPaymentType(type.value);
                }}
              >
                <span className="ceq-payment-type-label">{type.label}</span>
                <span className="ceq-payment-type-desc">{type.description}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="ceq-payment-method-group">
          <label className="ceq-label">Payment Method</label>
          <div className="ceq-payment-methods-inline">
            {paymentMethods.map((method) => (
              <button
                key={method.value}
                className={`ceq-payment-method-btn-inline ${paymentMethod === method.value ? 'active' : ''}`}
                onClick={() => setPaymentMethod(method.value)}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>

        {/* {error && (
          <div className="ceq-payment-error-inline">
            <span>⚠️ {error}</span>
          </div>
        )} */}

        <div className="ceq-inline-payment-footer">
          <button className="ceq-cancel-payment-btn" onClick={onCancel}>Cancel</button>
          <button 
            className="ceq-pay-now-btn" 
            onClick={handlePayment}
            disabled={processing}
          >
            {processing ? 'Processing...' : `Pay ₹${getAmountToPay().toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Quotation Component with Multiple Vendor Selection
const QuotationSection = ({ quotations, userId, authToken, onPaymentInitiated, onQuotationSelect }) => {
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [acceptedQuotation, setAcceptedQuotation] = useState(null);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState(null);

  const handleSelectQuotation = async (quote) => {
    setSelectedVendorId(quote.vendorId);
    setAcceptingQuoteId(quote.quotationId);
    
    const token = authToken;
    if (!token) {
      console.error('No auth token found');
      setAcceptingQuoteId(null);
      alert('Authentication failed. Please login again.');
      return;
    }

    try {
      const response = await axios.post(
        `https://backend.maamaas.com/catering/api/user/quotation/select/${quote.quotationId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Quotation acceptance response:', response.data);
      
      if (response.data && response.data.success) {
        setAcceptedQuotation(quote);
        if (onQuotationSelect) {
          onQuotationSelect(quote);
        }
        alert(`Quotation from ${quote.vendorName} has been accepted successfully!`);
      } else {
        alert(response.data?.message || 'Failed to accept quotation. Please try again.');
      }
    } catch (error) {
      console.error('Error accepting quotation:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unable to accept quotation. Please try again.';
      alert(`Error: ${errorMessage}`);
    } finally {
      setAcceptingQuoteId(null);
    }
  };

  const handlePaymentClick = (quotation) => {
    setSelectedQuotation(quotation);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (paymentResponse, quotation) => {
    setShowPayment(false);
    if (onPaymentInitiated) {
      onPaymentInitiated(quotation.leadId);
    }
    alert('Payment completed successfully!');
  };

  if (!quotations || quotations.length === 0) {
    return (
      <div className="ceq-quotations-empty">
        <p>No quotations available for this enquiry</p>
      </div>
    );
  }

  if (showPayment && selectedQuotation) {
    return (
      <InlinePayment
        quotation={selectedQuotation}
        onPaymentSuccess={handlePaymentSuccess}
        userId={userId}
        authToken={authToken}
        onCancel={() => setShowPayment(false)}
      />
    );
  }

  return (
    <div className="ceq-quotations-section">
      <div className="ceq-section-title-with-icon">
        <span>📋</span> Quotations from Vendors
        <span className="ceq-quote-count">{quotations.length} vendors</span>
      </div>
      
      <div className="ceq-quotations-grid">
        {quotations.map((quote) => {
          const isAccepted = acceptedQuotation?.quotationId === quote.quotationId || quote.status === 'SELECTED';
          const isAccepting = acceptingQuoteId === quote.quotationId;
          
          // Check if payment is already completed
          const isPaymentCompleted = quote.paymentStatus === 'COMPLETED' || 
                                     quote.remainingAmount === 0 || 
                                     quote.paymentStatus === 'SUCCESS' ||
                                     quote.orderStatus === 'PAYMENT_COMPLETED';
          
          return (
            <div 
              key={quote.quotationId} 
              className={`ceq-quotation-card ${selectedVendorId === quote.vendorId ? 'selected' : ''}`}
            >
              <div className="ceq-quotation-header">
                <div className="ceq-quotation-vendor">
                  <span className="ceq-vendor-icon">🏪</span>
                  <span className="ceq-vendor-name">{quote.vendorName}</span>
                </div>
                <QuotationStatusBadge status={isPaymentCompleted ? 'PAYMENT_COMPLETED' : (isAccepted ? 'SELECTED' : quote.status)} />
              </div>
              
              <div className="ceq-quotation-details">
                <div className="ceq-quote-detail-row">
                  <span className="ceq-quote-label">Quoted Amount:</span>
                  <span className="ceq-quote-value">₹{quote.quotedAmount?.toFixed(2)}</span>
                </div>
                <div className="ceq-quote-detail-row">
                  <span className="ceq-quote-label">Total Plates:</span>
                  <span className="ceq-quote-value">{quote.totalPlates} plates</span>
                </div>
                <div className="ceq-quote-detail-row">
                  <span className="ceq-quote-label">Per Plate (Veg/Non-Veg):</span>
                  <span className="ceq-quote-value">₹{quote.vegPerPlatePrice} / ₹{quote.nonVegPerPlatePrice}</span>
                </div>
                <div className="ceq-quote-detail-row">
                  <span className="ceq-quote-label">Taxes (CGST+SGST):</span>
                  <span className="ceq-quote-value">₹{(quote.cgstAmount + quote.sgstAmount).toFixed(2)}</span>
                </div>
                <div className="ceq-quote-detail-row">
                  <span className="ceq-quote-label">Delivery Fee:</span>
                  <span className="ceq-quote-value">₹{quote.deliveryFee?.toFixed(2)}</span>
                </div>
                <div className="ceq-quote-detail-row ceq-grand-total">
                  <span className="ceq-quote-label">Grand Total:</span>
                  <span className="ceq-quote-value">₹{quote.grandTotal?.toFixed(2)}</span>
                </div>
              </div>

              {quote.addOnPrices && quote.addOnPrices.length > 0 && (
                <div className="ceq-quotation-addons">
                  <div className="ceq-addons-label">Add-ons:</div>
                  <div className="ceq-addons-list">
                    {quote.addOnPrices.map((addon, idx) => (
                      <span key={idx} className="ceq-addon-tag">
                        {addon.addOnType.replace(/_/g, ' ')} x{addon.quantity} (₹{addon.totalAmount})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="ceq-quotation-actions">
                {!isAccepted && !isPaymentCompleted ? (
                  <button 
                    className="ceq-select-vendor-btn"
                    onClick={() => handleSelectQuotation(quote)}
                    disabled={isAccepting}
                  >
                    {isAccepting ? 'Accepting...' : 'Accept Quotation'}
                  </button>
                ) : isPaymentCompleted ? (
                  <>
                    <button className="ceq-select-vendor-btn payment-completed" disabled>
                      ✓ Payment Completed
                    </button>
                    <div className="ceq-payment-completed-message">
                      ✅ Payment has been successfully completed for this quotation
                    </div>
                  </>
                ) : (
                  <>
                    <button className="ceq-select-vendor-btn selected" disabled>
                      ✓ Quotation Accepted
                    </button>
                    <button 
                      className="ceq-pay-button"
                      onClick={() => handlePaymentClick(quote)}
                    >
                      💳 Make Payment
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Orders Component with Pending Payment
const OrdersSection = ({ userId, authToken }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState(null);
  const [companyName, setCompanyName] = useState("Maamaas Catering");


  const API_URL = `https://backend.maamaas.com/catering/api/user/getall/orders?userId=${userId}`;

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.status === 403) {
        throw new Error('Access forbidden. Please check your authentication token.');
      }
      
      if (response.status === 401) {
        throw new Error('Unauthorized. Please login again.');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      setError(err.message);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const toggleOrder = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handlePayRemaining = (order) => {
    const quotationForPayment = {
      quotationId: order.orderId,
      leadId: order.orderId,
      vendorName: order.vendorRegisteredName,
      grandTotal: order.total,
      partialAmount: order.amountRemaining * 0.1,
      remainingAmount: order.amountRemaining
    };
    setSelectedOrderForPayment(quotationForPayment);
    setShowPayment(true);
  };

  const handleViewInvoice = (order) => {
    setSelectedOrderForInvoice(order);
    setShowInvoice(true);
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    fetchOrders();
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED':
        return 'status-confirmed';
      case 'PENDING':
        return 'status-pending';
      case 'DELIVERED':
        return 'status-delivered';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  const getPaymentStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return 'payment-success';
      case 'PARTIALLY_PAID':
        return 'payment-partial';
      case 'PENDING':
        return 'payment-pending';
      case 'FAILED':
        return 'payment-failed';
      default:
        return 'payment-default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="ceq-loading-container">
        <div className="ceq-loading-spinner">
          <div className="ceq-spinner"></div>
          <p className="ceq-loading-text">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ceq-error-container">
        <div className="ceq-error-card">
          <p className="ceq-error-message">{error}</p>
          <button onClick={fetchOrders} className="ceq-retry-button">Try Again</button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="ceq-empty-container">
        <div className="ceq-empty-card">
          <p className="ceq-empty-title">No orders found</p>
          <p className="ceq-empty-subtitle">Your orders will appear here</p>
        </div>
      </div>
    );
  }

  if (showPayment && selectedOrderForPayment) {
    return (
      <div className="ceq-orders-section">
        <InlinePayment
          quotation={selectedOrderForPayment}
          onPaymentSuccess={handlePaymentSuccess}
          userId={userId}
          authToken={authToken}
          onCancel={() => setShowPayment(false)}
        />
      </div>
    );
  }

  return (
    <div className="ceq-orders-section">
      <div className="ceq-section-title-with-icon">
        <span>📦</span> Catering Orders
        <span className="ceq-order-count">{orders.length} orders</span>
      </div>

      <div className="ceq-orders-list">
        {orders.map((order) => (
          <div key={order.orderId} className="ceq-order-card">
            <div 
              className={`ceq-order-header ${expandedOrderId === order.orderId ? 'expanded' : ''}`}
              onClick={() => toggleOrder(order.orderId)}
            >
              <div className="ceq-order-header-content">
                <div className="ceq-order-summary">
                  <div className="ceq-order-id-badge">
                    <span className="ceq-order-label">Order #</span>
                    <span className="ceq-order-id">{order.orderId}</span>
                  </div>
                  <div className="ceq-order-vendor">
                    <span className="ceq-icon-store">🏪</span>
                    <span>{order.vendorRegisteredName || 'Vendor'}</span>
                  </div>
                  <div className="ceq-order-badges">
                    <span className={`ceq-status-badge ${getStatusBadgeClass(order.orderStatus)}`}>
                      {order.orderStatus || 'N/A'}
                    </span>
                    <span className={`ceq-payment-badge ${getPaymentStatusBadgeClass(order.paymentStatus)}`}>
                      {order.paymentStatus || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="ceq-order-quick-info">
                  <div className="ceq-quick-info-item">
                    <span>📅 {order.cateringDate}</span>
                  </div>
                  <div className="ceq-quick-info-item">
                    <span>🍽️ {order.orderItems?.reduce((sum, item) => sum + item.quantity, 0)} items</span>
                  </div>
                  <div className="ceq-quick-info-item ceq-total-amount">
                    <strong>{formatCurrency(order.total)}</strong>
                  </div>
                  <svg 
                    className={`ceq-expand-icon ${expandedOrderId === order.orderId ? 'ceq-rotated' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {expandedOrderId === order.orderId && (
              <div className="ceq-order-expanded">
                <div className="ceq-order-detail-grid">
                  <div className="ceq-detail-section">
                    <h4>📍 Delivery Address</h4>
                    <p>{order.deliveryAddress || 'Address not provided'}</p>
                    {order.distanceInKm > 0 && (
                      <span className="ceq-distance-badge">📏 {order.distanceInKm.toFixed(2)} km</span>
                    )}
                  </div>

                  <div className="ceq-detail-section">
                    <h4>📅 Event Details</h4>
                    <p><strong>Date:</strong> {order.cateringDate}</p>
                    <p><strong>Time:</strong> {order.cateringTime?.slice(0,5)}</p>
                  </div>
                </div>

                <div className="ceq-detail-section">
                  <h4>🍽️ Order Items</h4>
                  <div className="ceq-order-items">
                    {order.orderItems?.map((item, idx) => (
                      <div key={idx} className="ceq-order-item">
                        <div className="ceq-order-item-info">
                          <span className="ceq-item-name">{item.packageName}</span>
                          <span className="ceq-item-type">{item.packageType}</span>
                        </div>
                        <div className="ceq-order-item-details">
                          <span>Quantity: x{item.quantity}</span>
                          <span>{formatCurrency(item.packagePrice)}/plate</span>
                          <strong>{formatCurrency(item.packagePrice * item.quantity)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {order.addOns?.length > 0 && (
                  <div className="ceq-detail-section">
                    <h4>➕ Add-ons</h4>
                    <div className="ceq-addons-list">
                      {order.addOns.map((addon, idx) => (
                        <span key={idx} className="ceq-addon-tag">
                          {addon.addOnType?.replace(/_/g, ' ')} x{addon.quantity} - {formatCurrency(addon.totalAmount)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ceq-detail-section">
                  <h4>💰 Payment Breakdown</h4>
                  <div className="ceq-payment-breakdown">
                    <div><span>Subtotal:</span><span>{formatCurrency(order.subtotal)}</span></div>
                    <div><span>SGST:</span><span>{formatCurrency(order.sgst)}</span></div>
                    <div><span>CGST:</span><span>{formatCurrency(order.cgst)}</span></div>
                    <div><span>Delivery Fee:</span><span>{formatCurrency(order.deliveryFee)}</span></div>
                    <div><span>Platform Fee:</span><span>{formatCurrency(order.platformFeeAmount)}</span></div>
                    <div className="ceq-total-row"><span>Total:</span><span>{formatCurrency(order.total)}</span></div>
                    <div><span>Amount Paid:</span><span>{formatCurrency(order.amountPaid)}</span></div>
                    {order.amountRemaining > 0 && (
                      <>
                        <div className="ceq-remaining-row"><span>Pending Amount:</span><span>{formatCurrency(order.amountRemaining)}</span></div>
                        <button 
                          className="ceq-pay-remaining-btn"
                          onClick={() => handlePayRemaining(order)}
                        >
                          💳 Pay Pending ₹{order.amountRemaining.toFixed(2)}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="ceq-order-actions">
                  <button 
                    className="ceq-view-invoice-btn"
                    onClick={() => handleViewInvoice(order)}
                  >
                    🧾 View Invoice
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Invoice Modal */}
      {showInvoice && selectedOrderForInvoice && (
        <InvoiceModal 
          order={selectedOrderForInvoice} 
          onClose={() => setShowInvoice(false)} 
        />
      )}
    </div>
  );
};

// Main Component
const CateringEnquiriesList = ({ authToken: propAuthToken }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [quotations, setQuotations] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState({});
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [tokenError, setTokenError] = useState(false);
  const [activeTab, setActiveTab] = useState('enquiries');
  const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');

  const getAuthToken = () => {
    if (propAuthToken) return propAuthToken;
    const localStorageToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (localStorageToken) return localStorageToken;
    const sessionStorageToken = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
    if (sessionStorageToken) return sessionStorageToken;
    return null;
  };

  const fetchQuotationsForLead = async (leadId) => {
    const token = getAuthToken();
    if (!token) return;

    setLoadingQuotes(prev => ({ ...prev, [leadId]: true }));
    
    try {
      const response = await axios.get(
        `https://backend.maamaas.com/catering/api/user/lead/quotations/${leadId}/?userId=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );
      
      if (response.data && response.data.success && response.data.data) {
        setQuotations(prev => ({ ...prev, [leadId]: response.data.data }));
      } else if (response.data && Array.isArray(response.data)) {
        setQuotations(prev => ({ ...prev, [leadId]: response.data }));
      }
    } catch (err) {
      console.error(`Error fetching quotations for lead ${leadId}:`, err);
    } finally {
      setLoadingQuotes(prev => ({ ...prev, [leadId]: false }));
    }
  };

  useEffect(() => {
    const fetchEnquiries = async () => {
      setLoading(true);
      setError(null);
      setTokenError(false);
      
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setTokenError(true);
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(
          `https://backend.maamaas.com/catering/api/user/get/enquiry?userId=${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 15000
          }
        );
        
        let enquiriesData = [];
        if (response.data && Array.isArray(response.data)) {
          enquiriesData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          enquiriesData = response.data.data;
        } else if (response.data && response.data.enquiries && Array.isArray(response.data.enquiries)) {
          enquiriesData = response.data.enquiries;
        }
        
        const uniqueEnquiries = enquiriesData.filter((enquiry, index, self) => 
          index === self.findIndex((e) => e.id === enquiry.id)
        );
        
        setEnquiries(uniqueEnquiries);
      } catch (err) {
        console.error('Error fetching enquiries:', err);
        
        if (err.response) {
          if (err.response.status === 401 || err.response.status === 403) {
            setError('Authentication failed. Please check your token or login again.');
            setTokenError(true);
          } else {
            setError(`Server error: ${err.response.status} - ${err.response.data?.message || err.response.data?.error || 'Unknown error'}`);
          }
        } else if (err.request) {
          setError('Network error: Could not connect to server. Please check your connection.');
        } else {
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchEnquiries();
    } else {
      setError('No user ID provided');
      setLoading(false);
    }
  }, [userId]);

  const toggleExpand = async (id) => {
    const isExpanding = expandedId !== id;
    setExpandedId(expandedId === id ? null : id);
    
    if (isExpanding && !quotations[id]) {
      await fetchQuotationsForLead(id);
    }
  };

  const handlePaymentInitiated = (leadId) => {
    fetchQuotationsForLead(leadId);
  };

  const handleQuotationSelect = (quote) => {
    console.log('Selected vendor:', quote);
  };

  const getEventIcon = (eventType) => {
    switch(eventType?.toUpperCase()) {
      case 'BIRTHDAY':
        return '🎂';
      case 'WEDDING':
        return '💒';
      case 'ANNIVERSARY':
        return '💕';
      case 'CORPORATE':
        return '🏢';
      default:
        return '🎉';
    }
  };

  if (loading) {
    return (
      <div className="ceq-loading-container">
        <div className="ceq-loading-spinner">
          <div className="ceq-spinner"></div>
          <p className="ceq-loading-text">Loading enquiries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ceq-error-container">
        <div className="ceq-error-card">
          <svg className="ceq-error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="ceq-error-message">{error}</p>
          {tokenError && (
            <div className="ceq-token-error-help">
              <p className="ceq-token-help-text">Please make sure:</p>
              <ul className="ceq-token-help-list">
                <li>You are logged in properly</li>
                <li>Your session hasn't expired</li>
                <li>The token is stored as 'authToken' or 'token' in localStorage</li>
              </ul>
            </div>
          )}
          <button 
            onClick={() => window.location.reload()}
            className="ceq-retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ceq-app-container">
      {/* Tabs */}
      <div className="ceq-tabs">
        <button 
          className={`ceq-tab ${activeTab === 'enquiries' ? 'active' : ''}`}
          onClick={() => setActiveTab('enquiries')}
        >
          📋 Catering Enquiries
        </button>
        <button 
          className={`ceq-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          📦 Catering Orders
        </button>
      </div>

      {activeTab === 'enquiries' ? (
        <div className="ceq-enquiries-container">
          <div className="ceq-enquiries-list">
            {enquiries.length === 0 ? (
              <div className="ceq-empty-container">
                <div className="ceq-empty-card">
                  <svg className="ceq-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="ceq-empty-title">No catering enquiries found</p>
                  <p className="ceq-empty-subtitle">Create a new enquiry to get started</p>
                </div>
              </div>
            ) : (
              enquiries.map((enquiry) => (
                <div 
                  key={enquiry.id}
                  className="ceq-enquiry-card"
                >
                  <div 
                    className="ceq-card-header"
                    onClick={() => toggleExpand(enquiry.id)}
                  >
                    <div className="ceq-header-content">
                      <div className="ceq-header-info">
                        <div className="ceq-badge-container">
                          <span className="ceq-enquiry-id">
                            #{enquiry.id}
                          </span>
                          <StatusBadge status={enquiry.leadStatus} />
                        </div>
                        <div className="ceq-event-type">
                          {getEventIcon(enquiry.eventType)} {enquiry.eventType}
                        </div>
                        <div className="ceq-event-date-header">
                          📅 {formatDate(enquiry.eventDate)}
                        </div>
                      </div>
                      <div className="ceq-header-right">
                        <svg 
                          className={`ceq-expand-icon ${expandedId === enquiry.id ? 'ceq-rotated' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {expandedId === enquiry.id && (
                    <div className="ceq-expanded-content">
                      <div className="ceq-detail-table">
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">Customer Name</div>
                          <div className="ceq-table-value">{enquiry.fullName}</div>
                          <div className="ceq-table-label">Email</div>
                          <div className="ceq-table-value">{enquiry.email}</div>
                        </div>
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">Phone</div>
                          <div className="ceq-table-value">{enquiry.phoneNumber}</div>
                          <div className="ceq-table-label">Event Time</div>
                          <div className="ceq-table-value">{formatTime(enquiry.eventTime)}</div>
                        </div>
                        {enquiry.fromDate && enquiry.toDate && (
                          <div className="ceq-table-row">
                            <div className="ceq-table-label">Event Duration</div>
                            <div className="ceq-table-value" colSpan="3">
                              {formatDate(enquiry.fromDate)} - {formatDate(enquiry.toDate)}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="ceq-detail-table">
                        <div className="ceq-table-title">Food Details</div>
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">Veg Plates</div>
                          <div className="ceq-table-value">{enquiry.vegPlates}</div>
                          <div className="ceq-table-label">Non-Veg Plates</div>
                          <div className="ceq-table-value">{enquiry.nonVegPlates}</div>
                        </div>
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">Total Plates</div>
                          <div className="ceq-table-value">{enquiry.vegPlates + enquiry.nonVegPlates}</div>
                          <div className="ceq-table-label">Budget</div>
                          <div className="ceq-table-value">₹{enquiry.budget?.toLocaleString()}</div>
                        </div>
                      </div>

                      <div className="ceq-detail-table">
                        <div className="ceq-table-title">🍽️ Selected Dishes</div>
                        <SelectedDishesSection items={enquiry.items} />
                      </div>

                      {enquiry.addOns && enquiry.addOns.length > 0 && (
                        <div className="ceq-detail-table">
                          <div className="ceq-table-title">Add-ons</div>
                          <div className="ceq-addons-table">
                            {enquiry.addOns.map((addon, idx) => (
                              <div key={idx} className="ceq-addon-row">
                                <span className="ceq-addon-name">
                                  {addon.addOnType.replace(/_/g, ' ').toLowerCase()}
                                </span>
                                <span className="ceq-addon-qty">Quantity: {addon.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="ceq-detail-table">
                        <div className="ceq-table-title">📍 Address</div>
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">Full Address</div>
                          <div className="ceq-table-value" colSpan="3">{enquiry.fullAddress}</div>
                        </div>
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">City</div>
                          <div className="ceq-table-value">{enquiry.city}</div>
                          <div className="ceq-table-label">State</div>
                          <div className="ceq-table-value">{enquiry.state}</div>
                        </div>
                        <div className="ceq-table-row">
                          <div className="ceq-table-label">Pincode</div>
                          <div className="ceq-table-value">{enquiry.pincode}</div>
                          <div className="ceq-table-label">Country</div>
                          <div className="ceq-table-value">{enquiry.country}</div>
                        </div>
                      </div>

                      {enquiry.additionalRequests && (
                        <div className="ceq-detail-table">
                          <div className="ceq-table-title">💬 Special Requests</div>
                          <div className="ceq-requests-text">{enquiry.additionalRequests}</div>
                        </div>
                      )}

                      {loadingQuotes[enquiry.id] ? (
                        <div className="ceq-loading-quotes">
                          <div className="ceq-spinner-small"></div>
                          <span>Loading quotations...</span>
                        </div>
                      ) : (
                        <QuotationSection
                          quotations={quotations[enquiry.id]}
                          userId={userId}
                          authToken={getAuthToken()}
                          onPaymentInitiated={handlePaymentInitiated}
                          onQuotationSelect={handleQuotationSelect}
                        />
                      )}

                      <div className="ceq-meta-info">
                        <span>Created: {new Date(enquiry.createdAt).toLocaleString()}</span>
                        {enquiry.updatedAt !== enquiry.createdAt && (
                          <span> • Updated: {new Date(enquiry.updatedAt).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <OrdersSection userId={userId} authToken={getAuthToken()} />
      )}
    </div>
  );
};

export default CateringEnquiriesList;